import { useCallback, useEffect, useMemo } from "react";

const TYPE_STYLES = {
  AGENT_MATCH: { label: "Agent Match", classes: "bg-primary/10 text-primary_dark" },
  BUYER_MATCH: { label: "Buyer Match", classes: "bg-blue/10 text-blue" },
  NEW_LISTING: { label: "New Listing", classes: "bg-yellow/20 text-secondary" },
  LISTING_FAVORITED: { label: "Favorited", classes: "bg-rose-100 text-rose-600" },
  ADMIN_BROADCAST: { label: "Broadcast", classes: "bg-secondary/10 text-secondary" },
};

const formatTimestamp = (raw) => {
  if (!raw) return "";
  const d = new Date(+raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const NotificationDetailModal = ({ notification, onClose, recipient, kind, status }) => {
  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const typeMeta = useMemo(() => {
    if (!notification?.type) return null;
    return (
      TYPE_STYLES[notification.type] || {
        label: notification.type,
        classes: "bg-gray-100 text-gray-600",
      }
    );
  }, [notification?.type]);

  const listingLabel = useMemo(() => {
    if (!notification?.listing) return null;
    const { streetAddr, city, state, zip } = notification.listing;
    return [streetAddr, city, state, zip].filter(Boolean).join(", ");
  }, [notification?.listing]);

  if (!notification) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 relative">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 p-1"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="flex items-start gap-3 mb-3 pr-8">
          {typeMeta && (
            <span
              className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${typeMeta.classes}`}
            >
              {typeMeta.label}
            </span>
          )}
          {kind && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full text-white bg-primary_dark">
              {kind}
            </span>
          )}
        </div>

        <h2 className="text-xl font-semibold text-gray-900 m-0">
          {notification.title || "Notification"}
        </h2>
        <div className="text-xs text-gray-400 mt-1 mb-4">
          {formatTimestamp(notification.createdAt)}
        </div>

        <p className="text-sm text-secondary leading-relaxed whitespace-pre-wrap m-0">
          {notification.body || "(No additional message)"}
        </p>

        {listingLabel && (
          <div className="mt-5 pt-4 border-t border-gray-100 text-xs text-gray-500">
            <span className="font-semibold uppercase tracking-wider mr-2">Listing</span>
            {listingLabel}
          </div>
        )}

        {(recipient || status) && (
          <div className="mt-5 pt-4 border-t border-gray-100 text-xs text-gray-500 flex flex-wrap gap-x-8 gap-y-2">
            {recipient && (
              <div>
                <span className="font-semibold uppercase tracking-wider mr-2">
                  Recipient
                </span>
                {recipient}
              </div>
            )}
            {status && (
              <div>
                <span className="font-semibold uppercase tracking-wider mr-2">
                  Status
                </span>
                {status}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationDetailModal;
